import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  CloseButton,
} from '@headlessui/react';
import DButton from '@/components/Global/DButton';
import useDanteApi from '@/hooks/useDanteApi';
import * as plansService from '@/services/plans.service';
import { useUserStore } from '@/stores/user/userStore';
import useLayoutStore from '@/stores/layout/layoutStore';
import clsx from 'clsx';
import { trackAddonSubscription } from '@/helpers/analytics';
import DowngradeFlowModal from '@/v3-dashboard/components/DowngradeFlowModal';
import { trackBlackFridayModal } from '@/services/user.service';
import PlanCardHeader from '@/components/DModalPlans/PlanCardHeader';
import PlanCardPricing from '@/components/DModalPlans/PlanCardPricing';
import PlanCardButton from '@/components/DModalPlans/PlanCardButton';
import PlanCardFeatures from '@/components/DModalPlans/PlanCardFeatures';
import CheckmarkIcon from '@/components/DModalPlans/CheckmarkIcon';
import TrustPilotStar from '@/components/DModalPlans/TrustPilotStar';
import CloseIcon from '@/shared/icons/CloseIcon';
import DanteLogoIcon from '@/shared/icons/DanteLogoIcon';
import mixpanel from '@/utils/mixpanel';
import { useNavigate } from 'react-router-dom';

// Define the features list by plan
const FREE_FEATURES = [
  { label: '1 AI Agent' },
  { label: '1,000 messages/month', hasTooltip: true },
  { label: '100+ Languages' },
  { label: 'Basic LLMs' },
  { label: 'Email Support' },
  { label: 'Custom Styling' },
  { label: 'Custom Personalities' },
  { label: 'AI Agent Deployment' },
];

const STARTER_FEATURES = [
  // { label: 'Build 5 AI Voice Agents', underlineTooltip:true, content: 'Take your AI to the next level — build an AI voice agent. This is more than an agent. It\'s a fully interactive voice assistant powered by real-time AI, designed to engage, persuade, and convert — all through natural conversation and available via browser or direct phone call.', showNewBadge: true },
  { label: '2 Agents' },
  { label: 'Up to 12,000 responses', hasTooltip: true },
  { label: '25M Characters Memory/Agent' },
  { label: 'Fast LLMs' },
  // { label: '0 team member seats' },
  // { label: 'Realtime AI voice access' },
  // { label: 'Custom styling and personalities' },
  // { label: 'Embed unlimited websites' },
  // { label: 'Unlimited links to train on' },
  // { label: 'Unlimited files to train on' },
  // { label: 'Chat records' },
  // { label: 'Enhanced agent security' },
  // { label: '100+ languages' },
  { label: 'Zapier integration'},
  { label: 'Conversations History' },
  { label: 'Analytics'},
];

const ADVANCED_FEATURES = [
  // { label: 'Build 10 AI Voice Agents', underlineTooltip:true, content: 'Take your AI to the next level — build an AI voice agent. This is more than an agent. It\'s a fully interactive voice assistant powered by real-time AI, designed to engage, persuade, and convert — all through natural conversation and available via browser or direct phone call.', showNewBadge: true },
  // { label: 'Access to advanced LLM models' },
  { label: 'Everything in Starter, plus:' },
  { label: '10 Agents' },
  { label: 'Up to 50,000 responses', hasTooltip: true },
  { label: '50M Characters Memory/Agent' },
  { label: 'Remove "Powered by Dante AI"' },
  { label: 'AI Lead Generation' },
  { label: 'WhatsApp Integration' },
  { label: 'Human Handover (10 seats)' },
  { label: 'Team Management (3 seats)' },
  // { label: 'Advanced integrations', underlineTooltip: true, content: 'Whatsapp and Intercom integration' },
  { label: '5 Custom Domains', hasTooltip: true, content: ''},
  { label: 'Custom CSS for Agent Styling' },
  // { label: 'Conversation rate limiting' },
  // { label: "Memory auto-refresh" },
  // { label: 'Unanswered message recognition' },
  // { label: 'One-on-one video call support' },

];

const PRO_FEATURES = [
  { label: 'Everything in Advanced, plus:' },
  // { label: 'Build 50 AI Voice Agents', underlineTooltip:true, content: 'Take your AI to the next level — build an AI voice agent. This is more than an agent. It\'s a fully interactive voice assistant powered by real-time AI, designed to engage, persuade, and convert — all through natural conversation and available via browser or direct phone call.', showNewBadge: true },
  // { label: 'Access to all LLM models' },
  { label: '50 Agents' },
  { label: 'Up to 330,000 responses', hasTooltip: true },
  { label: '200M Characters Memory/Agent' },
  { label: 'API Integration' },
  { label: 'Advanced LLMs' },
  { label: 'Use your own Open AI API' },
  { label: 'Dedicated Account Manager' },
  { label: 'Team Management (10 seats)' },
  // { label: 'Custom integrations', underlineTooltip: true, content: 'Integrate your custom needs for your brand' },
  { label: '50 Custom Domains', hasTooltip: true, content: ''},
  // { label: 'Built with resellers in mind' },
  // { label: 'Dedicated account manager' },
];

// Tooltip content for messages feature
const MESSAGES_TOOLTIP =
  'This is the maximum number of messages your AI agent will send per month, depending on which LLM you use. For a full breakdown of LLM message costs, visit the FAQ here.';

// Plan descriptions
const PLAN_DESCRIPTIONS = {
  Starter: 'For small businesses',
  Advanced: 'For businesses wanting more robust AI features',
  Pro: 'For businesses wanting more advanced AI',
};

// Billing periods
const BILLING_PERIODS = [
  { id: 'monthly', label: 'Monthly' },
  { id: 'yearly', label: 'Annual', badge: '2 months free' }
];

// Addons for weekly plan
const WEEKLY_ADDONS = [
  { id: 'remove_powered_by_dante_ai', name: 'Remove Powered by Dante AI', price: 29, description: 'White-label your AI agent', addon_id: '' },
  { id: 'extra_chatbot', name: 'Add Agent', price: 5, description: 'Add one extra AI agent', addon_id: '' },
  { id: 'extra_seat', name: 'Add Team Member', price: 9, description: 'Add one additional team member', addon_id: '' },
];

const DModalPlans = ({ isOpen, onClose, title, subtitle, triggerSource}) => {
  const user = useUserStore((state) => state.user);
  const isDarkMode = useLayoutStore((state) => state.isDarkMode);
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [nextTier, setNextTier] = useState(null);
  const [isDowngradeModalOpen, setIsDowngradeModalOpen] = useState(false);
  const [isContentReady, setIsContentReady] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  const [loadingTierId, setLoadingTierId] = useState(null);
  const [loadingDowngradeTierId, setLoadingDowngradeTierId] = useState(null);
  const { data: tiers, isLoading } = useDanteApi(plansService.getTiers);
  const { data: plans } = useDanteApi(plansService.getPlans);
  const isSafariBrowser = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    return /safari/i.test(ua) && !/chrome|crios|chromium|android/i.test(ua);
  }, []);

  const openCheckoutUrl = (url) => {
    if (!url) return;
    window.location.assign(url);
  };

  // Track modal opened
  useEffect(() => {
    if (isOpen) {
      mixpanel.track('Plans Modal Opened', { current_tier: user?.tier_type, trigger_source: triggerSource || 'modal' });
    }
  }, [isOpen]);

  // Control when to show content after data is loaded
  useEffect(() => {
    if (!isLoading && tiers) {
      // Add a small delay to ensure smooth animation
      const timer = setTimeout(() => {
        setIsContentReady(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setIsContentReady(false);
    }
  }, [isLoading, tiers, isOpen]);

  const period = selectedPeriod;
  const blackFridayOfferType = user?.black_friday_offer_type || '';

  const getPlanSavings = (yearlyPrice, monthlyPrice, weeklyPrice) => {
    if (period === 'yearly') {
      let priceYearly = yearlyPrice;
      let priceMonthly = monthlyPrice;
      return Math.round(priceMonthly * 12 - priceYearly);
    } else if (period === 'monthly') {
      let priceWeekly = weeklyPrice
      // if (priceWeekly) {
        return Math.round((priceWeekly * 4) - monthlyPrice);
      // }
    }
    return 0;
  };

  const handleUpgrade = async (tierId, tierType) => {
    setLoadingTierId(tierId);
    mixpanel.track('Plan Upgrade Initiated', { current_tier: user?.tier_type, target_tier: tierType, billing_period: selectedPeriod, price: tiers?.[selectedPeriod]?.find(tier => tier.id === tierId)?.price });

    try {
      const response = await plansService.getCheckoutSession(tierId);

      if (response.status === 200) {
        openCheckoutUrl(response.data.checkout_session.url);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingTierId(null);
    }
  };

  const handleDowngrade = (tier) => {
    setLoadingDowngradeTierId(tier.id);
    setNextTier(tier);
    setIsDowngradeModalOpen(true);
    mixpanel.track('Plan Downgrade Initiated', { current_tier: user?.tier_type, target_tier: tier?.name?.toLowerCase() });
  };

  const handleContactSales = async () => {
    const email = 'hello@dante-ai.com';

    try {
      // Try to copy email to clipboard first
      await navigator.clipboard.writeText(email);
      setEmailCopied(true);

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setEmailCopied(false);
      }, 2000);

      // Try to open mailto as well
      window.location.href = `mailto:${email}?subject=Contact%20Us`;
    } catch (err) {
      // If clipboard fails, fallback to just mailto
      console.log('Clipboard API not available, using mailto only');
      window.location.href = `mailto:${email}?subject=Contact%20Us`;
    }
  };

  const handleBookCall = () => {
    window.open('https://calendly.com/jetelira/enterprise-introduction?month=2026-01', '_blank', 'noopener,noreferrer');
  };

  // Filter tiers based on user's current tier
  const getFilteredTiers = () => {
    const tierType = user?.tier_type || 0;
    const tierPeriod = user?.tier_key?.period || 0;
    if (tierType === 'pro' || tierType === 'professional' || tierType === 'ultra' || tierType === 'business') {
      return tiers?.[period]?.filter(tier => tier.name.toLowerCase() !== 'enterprise' && tier.name.toLowerCase() !== 'free') || [];
    } else if (tierType === 'free') { // Free tier
      return tiers?.[period]?.filter(tier =>
        ['Starter', 'Advanced', 'Pro', 'entry', 'premium', 'ultra', 'professional', 'enterprise', 'business'].includes(tier.name)
      ) || [];
    } else if (tierType === 'starter' || tierType === 'entry') { // Starter tier
      if (tierPeriod === 'weekly') {
        if (period === 'monthly' || period === 'yearly') {
          return tiers?.[period]?.filter(tier =>
            ['Starter', 'Advanced', 'Pro', 'entry', 'premium', 'ultra', 'professional', 'enterprise', 'business'].includes(tier.name)
          ) || [];
        }
        return tiers?.[period]?.filter(tier =>
          ['Advanced', 'Pro', 'premium', 'ultra', 'professional', 'enterprise', 'business'].includes(tier.name)
        ) || [];
      }
      return tiers?.[period]?.filter(tier =>
        ['Advanced', 'Pro', 'premium', 'ultra', 'professional', 'enterprise', 'business'].includes(tier.name)
      ) || [];
    } else if (tierType === 'advanced' || tierType === 'premium') { // Advanced tier
      if (tierPeriod === 'weekly') {
        if (period === 'monthly' || period === 'yearly') {
          return tiers?.[period]?.filter(tier =>
            ['Starter', 'Advanced', 'Pro', 'entry', 'premium', 'ultra', 'professional', 'enterprise', 'business'].includes(tier.name)
          ) || [];
        }
      }
      return tiers?.[period]?.filter(tier =>
        ['Pro', 'ultra', 'professional', 'enterprise', 'business'].includes(tier.name)
      ) || [];
    }
    return [];
  };

  const filteredTiers = getFilteredTiers();


  const handleAddon = async (addonKey) => {
    try {
      // Find the addon plan by key
      const addonPlan = plans?.find(plan => plan.key === addonKey);

      if (!addonPlan) {
        console.error('Addon plan not found for key:', addonKey);
        return;
      }

      const response = await plansService.getCheckoutSessionAddon(addonPlan.id);

      if (response.status === 200) {
        trackAddonSubscription({
          subscription_id: response.data.checkout_session.id,
          user_id: user.id,
          email: user.email,
          addon_name: addonPlan.name,
          addon_price: addonPlan.price,
        });
        openCheckoutUrl(response.data.checkout_session.url);
      }
    } catch (e) {
      console.error('Error handling addon:', e);
    }
  }

  // Determine if a tier should show the "Most Popular" label
  // Always show for Advanced plan regardless of monthly/yearly
  const isMostPopular = (tierName) => tierName === 'Advanced';

  // Show addons for Starter, Advanced, and Pro tiers
  const showAddons = ['starter', 'advanced', 'pro'].includes(user?.tier_type);

  // Get features for a tier
  const getFeaturesForTier = (tierName) => {
    if (tierName === 'Free') return FREE_FEATURES;
    if (tierName === 'Starter') return STARTER_FEATURES;
    if (tierName === 'Advanced') return ADVANCED_FEATURES;
    return PRO_FEATURES;
  };

  // Get card background color based on tier
  const getCardBackground = (tierName) => {
    // if (tierName === 'Advanced' || tierName === 'Starter') {
    //   return isDarkMode ? 'bg-[#1E1D30]' : 'bg-white';
    // }
    if (tierName === 'Advanced') {
      return isDarkMode ? 'bg-[#1E1D30]' : 'bg-white';
    }
    return 'bg-transparent';
  };

  // Don't open the modal until we have data
  const shouldOpenModal = Boolean(isOpen && (tiers || !isLoading));

  // Use provided title
  const modalTitle = title || 'Limited Offers';
  const modalSubtitle = subtitle || 'Access more power, faster responses, and scalable support with Dante AI.';

  if (!shouldOpenModal) return null;

  return (
    <>
      <Dialog
        open={shouldOpenModal}
        onClose={onClose}
        className="relative z-[999999]"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 duration-150 data-[closed]:opacity-0 data-[leave]:delay-100"
          style={{
            background: 'rgba(25, 32, 51, 0.5)',
            backdropFilter: 'blur(5px)',
            WebkitBackdropFilter: 'blur(5px)',
          }}
        />

        <div className="fixed inset-0 w-full ">
          <DialogPanel
            transition
            className={clsx(
              "w-full  overflow-hidden bg-oklch-100 ",
              "transition-all duration-300 data-[closed]:-translate-y-20 data-[closed]:opacity-0",
              " flex flex-col border border-grey-5 data-[enter]:delay-100 px-v3-lg pt-v3-md pb-v3-sm h-[100vh]",
              "max-h-full"
            )}
          >
            <div className="bg-oklch-100 flex flex-col items-center gap-0  h-full min-h-0">
              {/* Header */}
              <header
                className={clsx(
                  "flex items-center !gap-v3-xs !justify-center text-center w-full relative flex-shrink-0 min-h-[32px] md:min-h-0"
                )}
              >
                <div className="absolute left-0 top-0 flex items-center gap-v3-xs flex-shrink-0">
                  <button className='cursor-pointer' onClick={() => {onClose(); navigate('/')}}>
                  <DanteLogoIcon />
                  </button>
                </div>
                {/* <div className="flex flex-col gap-v3-xs sm:gap-v3-ms max-w-[85%] sm:max-w-[80%]">
                  {modalTitle && (
                    <DialogTitle
                      className={clsx(
                        "text-center w-full !font-[800]",
                        "text-base sm:text-xl font-medium leading-tight sm:leading-snug"
                      )}
                    >
                      {modalTitle}
                    </DialogTitle>
                  )}
                </div> */}
                <CloseButton className="absolute right-0 top-0">
                  <CloseIcon width={24} height={24} />
                </CloseButton>
              </header>

              {/* Content */}
              {!isLoading && isContentReady && (
                <main 
                  onTouchMove={(e) => e.stopPropagation()}
                  className={clsx(
                    'text-base overflow-y-auto overflow-x-hidden no-scrollbar ',
                    'flex flex-col',
                    'animate-fadeIn',
                    'transition-opacity duration-500 ease-out',
                    'flex-1 min-h-0',
                    'touch-pan-y overscroll-contain',
                    'max-w-[1200px] w-full'
                  )}
                  style={{
                    WebkitOverflowScrolling: 'touch'
                  }}
                >
          <div className="flex justify-center items-center gap-v3-sm self-stretch pb-v3-md pt-[12px]">
            <div className="flex gap-v3-xs items-center">
              {Array.from({ length: 5 }, (_, index) => (
                <TrustPilotStar key={index} />
              ))}
            </div>
            <span className="font-inter-variable text-[15px] font-regular leading-[24px] text-v3-black-max text-center">
              Trusted by 100,000+ businesses worldwide
            </span>
          </div>
          {/* Billing period tabs */}
          <div className="flex items-center justify-center pb-v3-md">
            <div className="bg-white rounded-full shadow-v3-light p-1 relative">
              {BILLING_PERIODS.map((billingPeriod) => (
                <button
                  key={billingPeriod.id}
                  onClick={() => {
                    setSelectedPeriod(billingPeriod.id);
                    mixpanel.track('Billing Period Changed', { period: billingPeriod.id });
                  }}
                  className={clsx(
                    'px-v3-md py-v3-xs text-xs font-medium rounded-full transition-all duration-200',
                    selectedPeriod === billingPeriod.id
                      ? 'bg-purple-10 text-purple-700'
                      : 'text-v3-black-secondary hover:bg-v3-black-card'
                  )}
                >
                  {billingPeriod.label}
                  {billingPeriod.badge && selectedPeriod === billingPeriod.id && (
                    <span className="absolute cursor-default top-full left-0 mt-1 md:top-1/2 md:-translate-y-1/2 md:left-auto md:translate-x-0 right-0 md:-right-24 md:mt-0 text-[10px] bg-v3-green-light text-v3-green-dark px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap z-10 max-w-[100px] md:max-w-none mx-auto">
                      {billingPeriod.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Plans grid */}
          <div
            className={clsx(
              'grid gap-v3-md grid-cols-1 sm:grid-cols-2',
              showAddons && selectedPeriod === 'weekly'
              ? filteredTiers.length === 1
                ? 'lg:grid-cols-2 xl:grid-cols-2'
                : filteredTiers.length === 2
                ? 'lg:grid-cols-3 xl:grid-cols-3'
                : filteredTiers.length === 3
                ? 'lg:grid-cols-4 xl:grid-cols-4'
                : 'lg:grid-cols-5 xl:grid-cols-5'
              : filteredTiers.length === 1
              ? 'lg:grid-cols-1 xl:grid-cols-1 max-w-md mx-auto'
              : filteredTiers.length === 2
              ? 'lg:grid-cols-2 xl:grid-cols-2'
              : filteredTiers.length === 3
              ? 'lg:grid-cols-3 xl:grid-cols-3'
                : 'lg:grid-cols-4 xl:grid-cols-4',
              'h-fit md:h-[680px]'
            )}
          >
            {/* Addons section - only shown for weekly plan and Starter/Advanced/Pro tiers */}
            {showAddons && selectedPeriod === 'weekly' && (
              <div className="flex flex-col gap-v3-md animate-fadeInUpDelayed1">
                <div className="text-center mb-v3-xs pt-v3-lg">
                  <h3 className="text-xl font-medium text-v3-black-max">Instant Add-ons</h3>
                  <p className="text-xs text-v3-black-secondary">Enhance your current plan with feature add-ons.</p>
                </div>

                {/* Addons as separate boxes */}
                {WEEKLY_ADDONS.map((addon) => (
                  <div
                    key={addon.id}
                    className="bg-white rounded-v3-sm py-v3-md px-v3-lg border border-v3-black-border flex flex-col gap-v3-sm shadow-v3-light transition-all duration-300 hover:scale-[1.01] overflow-hidden"
                  >
                    <div className="flex flex-col gap-0">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-medium text-v3-black-max">{addon.name}</span>
                        <span className="text-sm font-bold text-v3-black-max">${addon.price}/month</span>
                      </div>
                      <p className="text-xs text-v3-black-secondary leading-none">{addon.description}</p>
                    </div>
                    <DButton
                      fullWidth
                      className="py-[15px] px-v3-ms text-white rounded-full mt-auto"
                      style={{
                        background: 'linear-gradient(87deg, rgb(100, 116, 255) -15.34%, rgb(128, 120, 253) 21.77%, rgb(207, 192, 254) 83.94%) 0% 0% / 123.26% 100% border-box'
                      }}
                      onClick={() => handleAddon(addon.id)}
                    >
                      {(user?.tier_type === 'advanced' || user?.tier_type === 'pro') && addon.id === 'remove_powered_by_dante_ai' ? 'This is already included in your plan' : 'Add'}
                    </DButton>
                  </div>
                ))}
              </div>
            )}

            {filteredTiers.map((tier, index) => {
              const isAdvanced = tier.name === 'Advanced';
              const isStarter = tier.name === 'Starter';
              const features = getFeaturesForTier(tier.name);
              const cardBackground = getCardBackground(tier.name);

              return (
                <div
                  key={tier.id}
                  className={clsx(
                    'rounded-v3-sm flex flex-col relative',
                    cardBackground,
                    isAdvanced ? 'shadow-v3-light' : 'shadow-none',
                    'p-v3-md',
                    selectedPeriod === 'weekly'
                      ? index === 0
                        ? 'animate-fadeInUpDelayed2'
                        : 'animate-fadeInUpDelayed3'
                      : index === 0
                      ? 'animate-fadeInUpDelayed1'
                      : index === 1
                      ? 'animate-fadeInUpDelayed2'
                      : 'animate-fadeInUpDelayed3',
                    filteredTiers.length === 1 && 'mx-auto',
                    'h-auto sm:h-full'
                  )}
                >
                  {/* Top Section - Same for all plans */}
                  <div className="flex flex-col flex-shrink-0">
                    <PlanCardHeader
                      tierName={tier.name}
                      isMostPopular={isMostPopular(tier.name)}
                    />

                    <PlanCardPricing
                      tier={tier}
                      period={period}
                      blackFridayOfferType={blackFridayOfferType}
                      selectedPeriod={selectedPeriod}
                      getPlanSavings={getPlanSavings}
                      tiers={tiers}
                    />

                    <PlanCardButton
                      user={user}
                      tier={tier}
                      isAdvanced={isAdvanced}
                      loadingTierId={loadingTierId}
                      loadingDowngradeTierId={loadingDowngradeTierId}
                      handleUpgrade={handleUpgrade}
                      handleDowngrade={handleDowngrade}
                      period={period}
                    />
                  </div>

                  {/* Bottom Section - Scrollable Features List */}
                  <PlanCardFeatures features={features} />
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-v3-sm self-stretch py-v3-xm">
            <div className="flex items-center gap-v3-xs ">
              <CheckmarkIcon />
              <span className="font-inter-variable text-sm font-regular leading-[22px] text-v3-black-max">
                Cancel anytime, no commitments
              </span>
            </div>
            <a
              href="https://www.dante-ai.com/pricing"
              className="ml-auto h-10 py-[10px] px-v3-md flex flex-col justify-center items-center text-v3-purple-primary font-inter-variable text-sm font-medium leading-[20px] text-center hover:text-v3-purple-dark transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              View detailed comparison →
            </a>
          </div>

          {/* Enterprise section */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center self-stretch p-v3-xs  border-t border-v3-black-inactive animate-fadeInUpDelayed3 gap-v3-sm">
            <div className="flex flex-col items-start gap-v3-xs">
              <h3 className="font-inter-variable text-v3-h5 font-bolder leading-[28px] text-black">
                Enterprise
              </h3>
              <p className="font-inter-variable text-sm font-regular leading-[22px] text-v3-black-secondary">
                Unlimited access with Dante AI engineering support for bespoke solutions.
              </p>
            </div>
           <div className="flex flex-col md:flex-row items-stretch md:items-center gap-v3-xs w-full md:w-auto">
             {/* <button onClick={handleBookCall}
              className="h-10 py-[10px] px-v3-md flex flex-col justify-center flex-shrink-0 items-center rounded-v3-ms bg-v3-purple-lightest font-inter-variable text-sm font-medium leading-[20px] text-v3-purple-primary hover:bg-v3-purple-lighter transition-colors w-full md:w-auto"
            >
              Book a call
            </button> */}
             <button
                onClick={handleContactSales}
              className="h-10 py-[10px] px-v3-md min-w-[145px] flex flex-col justify-center flex-shrink-0 items-center rounded-v3-ms bg-v3-purple-lightest font-inter-variable text-sm font-medium leading-[20px] text-v3-purple-primary hover:bg-v3-purple-lighter transition-colors w-full md:w-auto"
              >
                {emailCopied ? 'Email Copied!' : 'Contact Us'}
            </button>

           </div>
          </div>
                </main>
              )}
            </div>
          </DialogPanel>
        </div>
      </Dialog>
      <DowngradeFlowModal
        isOpen={isDowngradeModalOpen}
        onClose={() => {
          setIsDowngradeModalOpen(false);
          setLoadingDowngradeTierId(null);
        }}
        nextTier={nextTier}
        onDataLoaded={() => setLoadingDowngradeTierId(null)}
      />
    </>
  );
};

export default DModalPlans;
